import React, { useState } from "react";
import { Box, Flex } from "theme-ui";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import SelectDate from "components/forms/SelectDate";
import { themevals } from "theme/themevals";
import moment from "moment-timezone";

interface QuoteQuickEditModalProps {
  quote: any;
  onConfirm: (data: {
    serviceDate?: string;
    siteContactName?: string;
    siteContactEmail?: string;
    siteContactPhone?: string;
    projectName?: string;
  }) => void;
  onCancel: () => void;
  submitting?: boolean;
}

const QuoteQuickEditModal = ({ quote, onConfirm, onCancel, submitting = false }: QuoteQuickEditModalProps) => {
  const [projectName, setProjectName] = useState(quote?.projectName || "");
  const [serviceDateUnix, setServiceDateUnix] = useState<number | null>(
    quote?.serviceDate ? moment(quote.serviceDate).unix() : null
  );
  const [siteContactName, setSiteContactName] = useState(quote?.siteContactName || "");
  const [siteContactEmail, setSiteContactEmail] = useState(quote?.siteContactEmail || "");
  const [siteContactPhone, setSiteContactPhone] = useState(quote?.siteContactPhone || "");

  const hasChanges = () => {
    const orig = quote || {};
    if (projectName !== (orig.projectName || "")) return true;
    const origUnix = orig.serviceDate ? moment(orig.serviceDate).unix() : null;
    if (serviceDateUnix !== origUnix) return true;
    if (siteContactName !== (orig.siteContactName || "")) return true;
    if (siteContactEmail !== (orig.siteContactEmail || "")) return true;
    if (siteContactPhone !== (orig.siteContactPhone || "")) return true;
    return false;
  };

  const handleConfirm = () => {
    const payload: any = {};
    const orig = quote || {};
    if (projectName !== (orig.projectName || "")) payload.projectName = projectName;
    const origUnix = orig.serviceDate ? moment(orig.serviceDate).unix() : null;
    if (serviceDateUnix !== origUnix) {
      payload.serviceDate = serviceDateUnix ? moment.unix(serviceDateUnix).toISOString() : undefined;
    }
    if (siteContactName !== (orig.siteContactName || "")) payload.siteContactName = siteContactName;
    if (siteContactEmail !== (orig.siteContactEmail || "")) payload.siteContactEmail = siteContactEmail;
    if (siteContactPhone !== (orig.siteContactPhone || "")) payload.siteContactPhone = siteContactPhone;
    onConfirm(payload);
  };

  return (
    <Modal isOpen={true} toggle={onCancel} size="md" centered={true}>
      <ModalHeader toggle={onCancel}>
        <Box sx={{ fontFamily: "Rubik", fontSize: "16px", fontWeight: 500, color: themevals.colors.black }}>
          Edit Quote Details
        </Box>
        <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "2px" }}>
          Changes will create a new minor version. The current version will be preserved.
        </Box>
      </ModalHeader>
      <ModalBody>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <TextInput
            label="Project Name"
            value={projectName}
            onChange={(val: any) => setProjectName(val || "")}
            placeholder="Project name"
          />

          <SelectDate
            label="Service Date"
            value={serviceDateUnix || undefined}
            onChange={(val: any) => setServiceDateUnix(val || null)}
            usePortal={true}
          />

          <TextInput
            label="Site Contact Name"
            value={siteContactName}
            onChange={(val: any) => setSiteContactName(val || "")}
            placeholder="Contact name"
          />

          <Flex sx={{ gap: "12px" }}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="Site Contact Email"
                type="email"
                value={siteContactEmail}
                onChange={(val: any) => setSiteContactEmail(val || "")}
                placeholder="Contact email"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="Site Contact Phone"
                value={siteContactPhone}
                onChange={(val: any) => setSiteContactPhone(val || "")}
                placeholder="Contact phone"
              />
            </Box>
          </Flex>
        </Box>
      </ModalBody>
      <ModalFooter>
        <BasicButton
          type="button"
          outline={true}
          onClick={onCancel}
          scheme="secondary"
          styles={{ margin: "0 10px .25rem 0" }}
        >
          Cancel
        </BasicButton>
        <BasicButton
          type="button"
          scheme="primary"
          onClick={handleConfirm}
          $submitting={submitting}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
          disabled={!hasChanges()}
        >
          Save as New Version
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default QuoteQuickEditModal;
